import type { ReactNode } from 'react'
import { Text, View } from 'react-native'
import { SvgXml } from 'react-native-svg'

import type { IconName } from '../../../components/ui/Icon'
import { Button } from '../../../components/ui/Button'
import { Icon, symbolName } from '../../../components/ui/Icon'
import { RedoRecordingIcon } from '../../../components/ui/RedoRecordingIcon'
import { StopRecordingIcon } from '../../../components/ui/StopRecordingIcon'
import { colors } from '../../../theme'
import { body3, label2 } from '../../../theme/typography'

const NON_SCALING_TEXT_PROPS = {
  allowFontScaling: false,
  maxFontSizeMultiplier: 1,
} as const

const ICON_SIZE = 80
const BUTTON_ICON_SIZE = 16
const CONTENT_BOX_GAP = 18
const TEXT_CONTRAST = '#FEFBFB'

const SOUND_ICON_SVG = `<svg width="${ICON_SIZE}" height="${ICON_SIZE}" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
<circle opacity="0.5" cx="40" cy="40" r="40" fill="url(#paint0_linear_sound)"/>
<mask id="mask0_sound" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="20" y="21" width="40" height="40">
<rect x="20" y="21" width="40" height="40" fill="#D9D9D9"/>
</mask>
<g mask="url(#mask0_sound)">
<path d="M52.2221 40.9585C52.2221 38.4493 51.5324 36.183 50.1529 34.1597C48.7732 32.1367 46.9538 30.6529 44.6946 29.7085C44.3335 29.551 44.0742 29.3033 43.9167 28.9655C43.7592 28.6275 43.7499 28.2872 43.8888 27.9447C44.0371 27.5741 44.2987 27.3148 44.6737 27.1668C45.0487 27.0187 45.4307 27.0233 45.8196 27.1805C48.5879 28.338 50.8101 30.1598 52.4862 32.646C54.1621 35.1321 55 37.9029 55 40.9585C55 44.014 54.1621 46.7848 52.4862 49.271C50.8101 51.7571 48.5879 53.5789 45.8196 54.7364C45.4307 54.8936 45.0487 54.8982 44.6737 54.7501C44.2987 54.6021 44.0371 54.3428 43.8888 53.9722C43.7499 53.6297 43.7592 53.2894 43.9167 52.9514C44.0742 52.6136 44.3335 52.366 44.6946 52.2085C46.9538 51.264 48.7732 49.7803 50.1529 47.7572C51.5324 45.7339 52.2221 43.4676 52.2221 40.9585ZM31.6667 46.0001H26.3888C25.9907 46.0001 25.6597 45.8682 25.3958 45.6043C25.1319 45.3404 25 45.0094 25 44.6114V37.3889C25 36.9908 25.1319 36.6598 25.3958 36.396C25.6597 36.1321 25.9907 36.0001 26.3888 36.0001H31.6667L37.6388 30.028C38.074 29.5928 38.5764 29.4932 39.1458 29.7293C39.7153 29.9654 40 30.3936 40 31.0139V50.9864C40 51.6066 39.7153 52.0348 39.1458 52.271C38.5764 52.5071 38.074 52.4075 37.6388 51.9722L31.6667 46.0001ZM47.5 41.0001C47.5 42.3521 47.1713 43.5905 46.5138 44.7155C45.8565 45.8405 44.9492 46.7178 43.7917 47.3472C43.5508 47.4769 43.3217 47.47 43.1042 47.3264C42.8867 47.183 42.7779 46.9817 42.7779 46.7222V35.2222C42.7779 34.963 42.8867 34.7617 43.1042 34.618C43.3217 34.4747 43.5508 34.4678 43.7917 34.5972C44.9492 35.2361 45.8565 36.1297 46.5138 37.278C47.1713 38.4261 47.5 39.6668 47.5 41.0001Z" fill="white"/>
</g>
<defs>
<linearGradient id="paint0_linear_sound" x1="15" y1="8" x2="51" y2="80" gradientUnits="userSpaceOnUse">
<stop stop-color="#E30B5D"/>
<stop offset="1" stop-color="#291180"/>
</linearGradient>
</defs>
</svg>`

const MICROPHONE_ICON_SVG = `<svg width="64" height="64" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
<circle cx="40" cy="40" r="40" fill="#DD1818"/>
<g>
<path d="M40 43.6947C38.7408 43.6947 37.6807 43.2464 36.8196 42.3497C35.9584 41.4531 35.5279 40.3643 35.5279 39.0835V28.7781C35.5279 27.5434 35.9625 26.4939 36.8316 25.6297C37.7011 24.7656 38.7566 24.3335 39.9983 24.3335C41.2403 24.3335 42.2964 24.7656 43.1666 25.6297C44.0369 26.4939 44.4721 27.5434 44.4721 28.7781V39.0835C44.4721 40.3643 44.0415 41.4531 43.1804 42.3497C42.3193 43.2464 41.2591 43.6947 40 43.6947ZM38.6112 54.6114V50.5139C35.9815 50.19 33.7107 49.1114 31.7987 47.2781C29.8865 45.4447 28.7637 43.19 28.4304 40.5139C28.3748 40.1186 28.4776 39.7814 28.7387 39.5022C28.9996 39.2231 29.3282 39.0835 29.7246 39.0835C30.1212 39.0835 30.4515 39.22 30.7154 39.4931C30.9793 39.7664 31.1482 40.1021 31.2221 40.5002C31.574 42.6482 32.5876 44.4075 34.2629 45.7781C35.9379 47.1484 37.8469 47.8335 39.99 47.8335C42.1633 47.8335 44.0833 47.146 45.75 45.771C47.4166 44.396 48.4259 42.6391 48.7779 40.5002C48.8518 40.1021 49.0223 39.7664 49.2896 39.4931C49.5568 39.22 49.8878 39.0835 50.2825 39.0835C50.6775 39.0835 51.0046 39.2231 51.2637 39.5022C51.5232 39.7814 51.6251 40.1186 51.5696 40.5139C51.2362 43.1528 50.1204 45.3982 48.2221 47.2502C46.324 49.1021 44.0462 50.19 41.3887 50.5139V54.6114C41.3887 55.0047 41.2553 55.3346 40.9883 55.601C40.7211 55.8671 40.39 56.0002 39.995 56.0002C39.6003 56.0002 39.2709 55.8671 39.0071 55.601C38.7432 55.3346 38.6112 55.0047 38.6112 54.6114Z" fill="white"/>
</g>
</svg>`

type RecordActionButtonProps = {
  label: string
  onPress: () => void
  iconName?: IconName
  iconElement?: ReactNode
  variant: 'secondary' | 'primary' | 'neutral' | 'warning' | 'ghost'
  horizontalPadding?: number
  contentGap?: number
  disabled?: boolean
}

function RecordActionButton({
  label,
  onPress,
  iconName,
  iconElement,
  variant,
  horizontalPadding = 0,
  contentGap = 8,
  disabled = false,
}: RecordActionButtonProps) {
  const resolvedIconColor =
    variant === 'secondary' || variant === 'primary'
      ? TEXT_CONTRAST
      : colors.textHeader
  const leadingIcon =
    iconElement ??
    (iconName != null ? (
      <Icon name={iconName} size={BUTTON_ICON_SIZE} color={resolvedIconColor} />
    ) : null)

  return (
    <Button
      label={label}
      onPress={onPress}
      variant={variant}
      size="md"
      leadingIcon={leadingIcon}
      containerStyle={{
        paddingHorizontal: horizontalPadding,
        gap: contentGap,
      }}
      disabled={disabled}
    />
  )
}

export type RecordReadyStateProps = {
  instruction: string
  playPianoLabel: string
  playExampleLabel: string
  playingPianoLabel: string
  playingExampleLabel: string
  startRecordingLabel: string
  onPlayPiano: () => void
  onPlayExample: () => void
  onStartRecording: () => void
  status: {
    isExamplePlaying: boolean
    isHelperActionPending: boolean
    isPianoPlaying: boolean
    isStartingRecording: boolean
  }
}

export function RecordReadyState({
  instruction,
  playPianoLabel,
  playExampleLabel,
  playingPianoLabel,
  playingExampleLabel,
  startRecordingLabel,
  onPlayPiano,
  onPlayExample,
  onStartRecording,
  status,
}: RecordReadyStateProps) {
  const { isHelperActionPending, isPianoPlaying, isStartingRecording } = status

  return (
    <>
      <View className="items-center" style={{ gap: CONTENT_BOX_GAP }}>
        <SvgXml xml={SOUND_ICON_SVG} width={ICON_SIZE} height={ICON_SIZE} />
        <Text
          {...NON_SCALING_TEXT_PROPS}
          className="text-center"
          style={[body3, { color: colors.textBody }]}
        >
          {instruction}
        </Text>
      </View>
      <View style={{ gap: CONTENT_BOX_GAP }}>
        <RecordActionButton
          label={isPianoPlaying ? playingPianoLabel : playPianoLabel}
          onPress={onPlayPiano}
          variant="warning"
          iconName={symbolName(
            isPianoPlaying ? 'stop.circle' : 'play.circle',
            isPianoPlaying ? 'stop_circle' : 'play_circle',
          )}
          disabled={isHelperActionPending || isStartingRecording}
        />
        {/* <RecordActionButton
          label={isExamplePlaying ? playingExampleLabel : playExampleLabel}
          onPress={onPlayExample}
          variant="warning"
          iconName={symbolName(
            isExamplePlaying ? 'stop.circle' : 'play.circle',
            isExamplePlaying ? 'stop_circle' : 'play_circle',
          )}
          disabled={isHelperActionPending || isStartingRecording}
        /> */}
        <RecordActionButton
          label={startRecordingLabel}
          onPress={onStartRecording}
          variant="primary"
          iconName={symbolName('mic', 'mic')}
          disabled={isStartingRecording}
        />
      </View>
    </>
  )
}

export type RecordRecordingStateProps = {
  statusLabel: string
  elapsedLabel: string
  stopRecordingLabel: string
  onStopRecording: () => void
  isStopPending: boolean
}

export function RecordRecordingState({
  statusLabel,
  elapsedLabel,
  stopRecordingLabel,
  onStopRecording,
  isStopPending,
}: RecordRecordingStateProps) {
  return (
    <View className="items-center" style={{ gap: CONTENT_BOX_GAP }}>
      <SvgXml xml={MICROPHONE_ICON_SVG} width={64} height={64} />
      <Text
        {...NON_SCALING_TEXT_PROPS}
        style={[label2, { color: colors.textBody }]}
      >
        {statusLabel}
      </Text>
      <Text
        {...NON_SCALING_TEXT_PROPS}
        style={[body3, { color: colors.textBody }]}
      >
        {elapsedLabel}
      </Text>
      <RecordActionButton
        label={stopRecordingLabel}
        onPress={onStopRecording}
        variant="ghost"
        iconElement={
          <StopRecordingIcon
            color={colors.textHeader}
            size={BUTTON_ICON_SIZE}
          />
        }
        horizontalPadding={16}
        disabled={isStopPending}
      />
    </View>
  )
}

export type RecordSucceededStateProps = {
  statusLabel: string
  listenLabel: string
  stopPlaybackLabel: string
  redoLabel: string
  onListen: () => void
  onRedo: () => void
  isRecordedAudioPlaying: boolean
  isListeningPending: boolean
  hasRecordedClip: boolean
}

export function RecordSucceededState({
  statusLabel,
  listenLabel,
  stopPlaybackLabel,
  redoLabel,
  onListen,
  onRedo,
  isRecordedAudioPlaying,
  isListeningPending,
  hasRecordedClip,
}: RecordSucceededStateProps) {
  return (
    <View className="items-center" style={{ gap: CONTENT_BOX_GAP }}>
      <View
        className="rounded-full items-center justify-center"
        style={{ width: 64, height: 64, backgroundColor: colors.coreSuccess }}
      >
        <Icon
          name={symbolName('mic', 'mic')}
          size={32}
          color={colors.primaryForeground}
        />
      </View>
      <Text
        {...NON_SCALING_TEXT_PROPS}
        style={[label2, { color: colors.textHeader }]}
      >
        {statusLabel}
      </Text>
      <View className="w-full" style={{ gap: CONTENT_BOX_GAP }}>
        <RecordActionButton
          label={isRecordedAudioPlaying ? stopPlaybackLabel : listenLabel}
          onPress={onListen}
          variant="neutral"
          iconName={symbolName(
            isRecordedAudioPlaying ? 'stop.circle' : 'speaker.wave.3',
            isRecordedAudioPlaying ? 'stop_circle' : 'volume_up',
          )}
          horizontalPadding={16}
          disabled={isListeningPending || !hasRecordedClip}
        />
        <RecordActionButton
          label={redoLabel}
          onPress={onRedo}
          variant="warning"
          iconElement={
            <RedoRecordingIcon
              color={colors.textHeader}
              size={BUTTON_ICON_SIZE}
            />
          }
        />
      </View>
    </View>
  )
}
